<script setup lang="ts">
import { STRAHOVANIE_FRAME_BASE_URL, STRAHOVANIE_FRAME_PARAMS, STRAHOVANIE_SERVICES } from './constants'
import type { IStrahovanieFrameProps as IProps } from './types'
import { Button } from '~/components/ui/button'
import { ChevronRight, ShieldCheck } from 'lucide-vue-next'
import { Heading } from '~/components/ui/heading'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { Loader } from '~/components/ui/loader'

const props = defineProps<IProps>()
const { t, locale } = useI18n()

const isOpen = ref(false)
const isFrameLoading = ref(true)

const services = computed(() => {
  if (!props.insuranceType) {
    return STRAHOVANIE_SERVICES
  }
  return STRAHOVANIE_SERVICES.filter((service) => service.insuranceType === props.insuranceType)
})

const hasTabs = computed(() => services.value.length > 1)
const selectedServiceId = ref(services.value[0]?.id)

const selectedService = computed(() => {
  const service = services.value.find((service) => service.id === selectedServiceId.value)
  return service ?? services.value[0]
})

const frameUrl = computed(() => {
  if (!selectedService.value) {
    return ''
  }
  const frameLocale = locale.value === 'ky' ? 'kg' : 'ru'
  const params = new URLSearchParams({
    ...STRAHOVANIE_FRAME_PARAMS,
    locale: frameLocale,
    height: String(selectedService.value.frameMaxHeight)
  })
  return `${STRAHOVANIE_FRAME_BASE_URL}/${selectedService.value.path}?${params.toString()}`
})

const frameBody = useTemplateRef<HTMLElement>('frameBody')

// The iframe remounts on every dialog open and tab switch, so the loader is shown again.
// The new form starts from the top, which also keeps the loader in view.
watch([isOpen, frameUrl], () => {
  isFrameLoading.value = true
  frameBody.value?.scrollTo({ top: 0 })
})

const { trackEvent } = useAnalytics()

// Purchases happen inside the partner iframe and are not reported back, so opening
// the widget is the last step that can be measured
watch(isOpen, (open) => {
  if (open && selectedService.value) {
    trackEvent('insurance_widget_open', { insurance_type: selectedService.value.id })
  }
})

const dialogTitle = computed(() => {
  if (hasTabs.value || !selectedService.value) {
    return t('widget.strahovanie_frame.heading')
  }
  return t(selectedService.value.label)
})

const frameDescription = computed(() => (selectedService.value ? t(selectedService.value.hint) : ''))

const cardHeading = computed(() => {
  if (hasTabs.value || !selectedService.value) {
    return t('widget.strahovanie_frame.heading')
  }
  return t('widget.strahovanie_frame.service_heading', {
    service: t(selectedService.value.label)
  })
})

const cardDescription = computed(() => {
  if (hasTabs.value) {
    return t('widget.strahovanie_frame.description')
  }
  return frameDescription.value
})
</script>

<template>
  <div v-if="selectedService">
    <div
      class="relative overflow-hidden rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 p-5 text-white"
    >
      <ShieldCheck
        class="pointer-events-none absolute -right-6 -top-6 size-32 text-white/10"
        :stroke-width="1.5"
      />
      <div class="relative space-y-4">
        <div class="space-y-1.5 pr-10">
          <Heading tag="h3" class="h4 text-white">
            {{ cardHeading }}
          </Heading>
          <p class="text-sm leading-snug text-blue-100">
            {{ cardDescription }}
          </p>
        </div>
        <Button
          variant="white"
          class="w-full border-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
          @click="isOpen = true"
        >
          {{ $t('widget.strahovanie_frame.button') }}
          <ChevronRight class="size-4" />
        </Button>
      </div>
    </div>

    <Dialog v-model:open="isOpen">
      <DialogContent
        class="flex max-h-dvh w-full flex-col gap-0 rounded-none p-0 sm:max-h-[calc(100dvh-2rem)] sm:max-w-3xl sm:rounded-lg"
      >
        <div class="space-y-3 border-b px-6 py-4">
          <div class="space-y-1 pr-6">
            <DialogTitle>{{ dialogTitle }}</DialogTitle>
            <DialogDescription>{{ frameDescription }}</DialogDescription>
          </div>
          <Tabs v-if="hasTabs" v-model="selectedServiceId">
            <TabsList class="flex-wrap gap-2">
              <TabsTrigger
                v-for="service in services"
                :key="service.id"
                :value="service.id"
                class="text-sm"
              >
                <component :is="service.icon" class="size-4" />
                {{ $t(service.label) }}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div ref="frameBody" class="relative flex-1 overflow-y-auto">
          <iframe
            v-if="frameUrl"
            :key="frameUrl"
            :src="frameUrl"
            :title="t(selectedService.label)"
            class="block w-full border-0"
            :class="selectedService.frameClass"
            @load="isFrameLoading = false"
          />
          <Loader v-if="isFrameLoading" class="absolute inset-0 bg-white" />
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
